import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Icon from "@/components/Icon";
import { useLead } from "@/context/LeadContext";
import DivisionsCarousel from "@/components/DivisionsCarousel";
import {
  FOUNDERS, ABOUT_VALUES, ABOUT_STATS,
  ABOUT_STRATEGY, ABOUT_ROADMAP, INVESTOR_HIGHLIGHTS, INVESTOR_POINTS,
} from "@/data/content";

export default function AboutPage() {
  const { openLead } = useLead();
  const navigate = useNavigate();
  useEffect(() => { document.title = "About | Proactive UMS Corporate"; window.scrollTo(0, 0); }, []);

  return (
    <div className="ip" data-testid="about-page">
      <section className="dp-hero ip-hero">
        <div className="dp-hero__bg" style={{ backgroundImage: "url(/assets/hero-slide-4.png)" }} />
        <div className="dp-hero__veil" />
        <div className="container">
          <div className="dp-hero__inner">
            <span className="eyebrow">About Proactive UMS Corporate</span>
            <h1 className="dp-hero__title display">One Vision for Connected Care</h1>
            <p className="dp-hero__tagline">People. Technology. Better Tomorrows.</p>
            <p className="dp-hero__blurb">Proactive UMS Corporate unites behavioral health, home care, clinics, MedTech and AI under one intelligent platform — founded by clinical and technology leaders committed to a healthier, more connected tomorrow.</p>
            <div className="dp-hero__actions">
              <button className="btn btn--green" onClick={() => openLead("demo")} data-testid="about-demo">Request a Demo <Icon name="ArrowRight" size={16} /></button>
              <button className="btn btn--outline-dark" onClick={() => navigate("/contact")} data-testid="about-contact">Contact Our Team</button>
            </div>
          </div>
        </div>
      </section>

      {/* Divisions carousel */}
      <DivisionsCarousel />

      {/* Founders — moved to top, enhanced */}
      <section className="ip-founders2">
        <div className="container">
          <div className="sec-head sec-head--center">
            <span className="ip-kicker">Leadership</span>
            <h2 className="sec-head__title">Founders &amp; Ownership</h2>
            <p className="sec-head__sub">Equal co-founders — uniting clinical leadership and technology vision.</p>
          </div>
          <div className="fdr2" data-testid="founders-box">
            {FOUNDERS.map((f) => (
              <article className="fdr2__card" key={f.name} data-testid={`founder-${f.id}`}>
                <div className="fdr2__media">
                  <img src={f.photo} alt={f.name} className="fdr2__photo" />
                </div>
                <div className="fdr2__body">
                  <h3 className="fdr2__name">{f.name}</h3>
                  <span className="fdr2__role">{f.title}</span>
                  <p className="fdr2__bio">{f.bio}</p>
                  <div className="fdr2__tags">
                    {f.expertise.map((t) => <span className="fdr2__tag" key={t}>{t}</span>)}
                  </div>
                  {f.linkedin && (
                    <a className="fdr2__link" href={f.linkedin} target="_blank" rel="noopener noreferrer">
                      <Icon name="Linkedin" size={16} /> Connect on LinkedIn
                    </a>
                  )}
                </div>
              </article>
            ))}
          </div>
          <p className="founders-note">Co-founded and co-owned — shared vision, shared decisions, shared commitment to better care.</p>
        </div>
      </section>

      {/* Mission / Vision / Promise */}
      <section className="ip-mv">
        <div className="container">
          <div className="sec-head sec-head--center">
            <span className="ip-kicker">Purpose</span>
            <h2 className="sec-head__title">Why We Exist</h2>
          </div>
          <div className="ip-mv__grid ip-mv__grid--3">
            <div className="ip-mv__card">
              <span className="ip-mv__ic"><Icon name="Heart" size={24} /></span>
              <h3>Our Mission</h3>
              <p>To unify care, technology and operations into one intelligent healthcare ecosystem — making better, more affordable health accessible to every patient, provider and community we serve.</p>
            </div>
            <div className="ip-mv__card">
              <span className="ip-mv__ic"><Icon name="TrendingUp" size={24} /></span>
              <h3>Our Vision</h3>
              <p>A healthier, more connected tomorrow — where compassionate care and advanced technology work as one, everywhere care is needed and for everyone who needs it.</p>
            </div>
            <div className="ip-mv__card">
              <span className="ip-mv__ic"><Icon name="ShieldCheck" size={24} /></span>
              <h3>Our Promise</h3>
              <p>People first, always. We build technology that serves clinicians and patients — never the other way around — with security, transparency and trust at the core.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Story */}
      <section className="ip-story">
        <div className="container ip-story__grid">
          <div className="sec-head sec-head--stack">
            <h2 className="sec-head__title">Our Story<span className="sec-head__rule" /></h2>
          </div>
          <div className="ip-story__body">
            <p>Proactive UMS was founded on a simple belief: that healthcare works best when clinical expertise and modern technology move together. Our founders combined decades of hands-on clinical operations with deep experience in software, cloud and artificial intelligence.</p>
            <p>Today, the PUMS ecosystem brings six specialized divisions together on one connected platform — spanning behavioral health, home care, multi-specialty clinics, medical technology and the AI-driven infrastructure that unifies them all.</p>
            <p>We are building deliberately — proving value with real clinics and real patients first, then scaling the model division-by-division into a nationwide, connected standard of care.</p>
          </div>
        </div>
      </section>

      {/* Strategy pillars */}
      <section className="ip-strategy">
        <div className="container">
          <div className="sec-head sec-head--center">
            <span className="ip-kicker">Strategy</span>
            <h2 className="sec-head__title">How We Build</h2>
            <p className="sec-head__sub">Four pillars guiding every decision across the ecosystem.</p>
          </div>
          <div className="ip-strategy__grid">
            {ABOUT_STRATEGY.map((s) => (
              <div className="ip-strat" key={s.title} data-testid={`strategy-${s.title}`}>
                <span className="ip-strat__ic"><Icon name={s.icon} size={24} /></span>
                <h4>{s.title}</h4>
                <p>{s.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Roadmap */}
      <section className="ip-roadmap">
        <div className="container">
          <div className="sec-head sec-head--center">
            <span className="ip-kicker">Roadmap</span>
            <h2 className="sec-head__title">Our Path Forward</h2>
            <p className="sec-head__sub">A phased plan from founding clinics to a nationwide platform.</p>
          </div>
          <div className="ip-road">
            {ABOUT_ROADMAP.map((r) => (
              <div className="ip-road__step" key={r.phase} data-testid={`roadmap-${r.phase}`}>
                <span className="ip-road__num">{r.phase}</span>
                <h4>{r.title}</h4>
                <p>{r.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Investors */}
      <section className="ip-invest" id="investors">
        <div className="container">
          <div className="sec-head sec-head--center">
            <span className="ip-kicker">Investors</span>
            <h2 className="sec-head__title">The Opportunity</h2>
            <p className="sec-head__sub">Building the connected healthcare platform for the next decade.</p>
          </div>
          <div className="ip-invest__stats">
            {INVESTOR_HIGHLIGHTS.map((h) => (
              <div className="ip-invest__stat" key={h.label} data-testid={`invest-stat-${h.label}`}>
                <strong>{h.value}</strong><span>{h.label}</span>
              </div>
            ))}
          </div>
          <div className="ip-invest__grid">
            {INVESTOR_POINTS.map((p) => (
              <div className="ip-invest__point" key={p.title} data-testid={`invest-point-${p.title}`}>
                <span className="ip-invest__ic"><Icon name={p.icon} size={22} /></span>
                <div>
                  <h4>{p.title}</h4>
                  <p>{p.text}</p>
                </div>
              </div>
            ))}
          </div>
          <div className="ip-invest__cta">
            <div>
              <h3>Investor Relations</h3>
              <p>Interested in partnering with Proactive UMS Corporate? We would love to connect.</p>
            </div>
            <button className="btn btn--blue" onClick={() => openLead("contact")} data-testid="about-investor-contact">
              Contact Investor Relations <Icon name="ArrowRight" size={16} />
            </button>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="ip-values">
        <div className="container">
          <div className="sec-head sec-head--center">
            <span className="ip-kicker">Culture</span>
            <h2 className="sec-head__title">What We Stand For</h2>
          </div>
          <div className="ip-values__grid">
            {ABOUT_VALUES.map((v) => (
              <div className="ip-value" key={v.title}>
                <span className="ip-value__ic"><Icon name={v.icon} size={22} /></span>
                <h4>{v.title}</h4>
                <p>{v.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="dp-stats">
        <div className="container dp-stats__row">
          {ABOUT_STATS.map((s) => (
            <div className="dp-stat" key={s.label}><strong>{s.value}</strong><span>{s.label}</span></div>
          ))}
        </div>
      </section>

      <section className="dp-cta">
        <img src="/assets/cta-family.png" className="dp-cta__bg" alt="" />
        <div className="container dp-cta__inner">
          <div className="dp-cta__copy">
            <h2 className="dp-cta__title display">Let&rsquo;s build a healthier tomorrow.</h2>
            <p>Partner with the Proactive UMS ecosystem.</p>
          </div>
          <div className="dp-cta__actions">
            <button className="btn btn--green" onClick={() => openLead("demo")}>Request a Demo <Icon name="ArrowRight" size={16} /></button>
            <button className="btn btn--outline-dark" onClick={() => navigate("/contact")}><Icon name="Mail" size={16} /> Contact Our Team</button>
          </div>
        </div>
      </section>
    </div>
  );
}
